import { useEffect, useState } from 'react';
import { deriveInventoryStatus, INVENTORY_STATUS_LABEL } from '@/lib/inventoryStatus';
import { supabase } from '@/lib/supabase';
import type { InventoryStatus } from '@/lib/types';

interface InventoryRow {
  id: string;
  gift_product_id: string;
  quantity_expected: number | null;
  quantity_available: number;
  quantity_reserved: number | null;
  written_off_at: string | null;
  unit_cost_excl_btw: number | null;
  acquired_date: string | null;
  gift_products?:
    | { name: string; brand: string | null }
    | { name: string; brand: string | null }[]
    | null;
}

function productOf(row: InventoryRow): { name: string; brand: string | null } | null {
  if (!row.gift_products) return null;
  if (Array.isArray(row.gift_products)) return (row.gift_products[0] ?? null);
  return row.gift_products;
}

const STATUS_STYLE: Record<InventoryStatus, string> = {
  beschikbaar: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  gereserveerd: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  verwacht: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  op: 'bg-bmade-100 text-bmade-600 dark:bg-bmade-800 dark:text-bmade-300',
  afgeschreven: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};

export function InventoryPage() {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from('gift_inventory')
        .select(
          'id, gift_product_id, quantity_expected, quantity_available, quantity_reserved, written_off_at, unit_cost_excl_btw, acquired_date, gift_products(name, brand)'
        )
        .order('acquired_date', { ascending: false })
        .limit(100);
      if (error) setError(error.message);
      else setRows((data as InventoryRow[]) ?? []);
      setLoading(false);
    }
    void load();
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">Restvoorraad</h1>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          Producten over uit eerdere projecten. Inzetbaar in nieuwe pakketten, of doorzetten naar
          kortingsproducten als ze te oud worden.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-bmade-200 bg-white dark:border-bmade-800 dark:bg-bmade-900/40">
        <table className="w-full text-sm">
          <thead className="bg-bmade-100/60 text-left text-xs uppercase tracking-wider text-bmade-600 dark:bg-bmade-900/60 dark:text-bmade-400">
            <tr>
              <th className="px-4 py-3 font-medium">Product</th>
              <th className="px-4 py-3 font-medium">Merk</th>
              <th className="px-4 py-3 text-right font-medium">Beschikbaar</th>
              <th className="px-4 py-3 text-right font-medium">Verwacht</th>
              <th className="px-4 py-3 text-right font-medium">Gereserveerd</th>
              <th className="px-4 py-3 text-right font-medium">Kostprijs</th>
              <th className="px-4 py-3 font-medium">Datum binnen</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bmade-200 dark:divide-bmade-800">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-bmade-500">
                  Laden...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-bmade-500">
                  Nog geen restvoorraad geregistreerd.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const p = productOf(r);
                const status = deriveInventoryStatus(r);
                return (
                  <tr
                    key={r.id}
                    className="transition-colors hover:bg-bmade-50 dark:hover:bg-bmade-900/60"
                  >
                    <td className="px-4 py-3 font-medium text-bmade-950 dark:text-white">
                      {p?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-bmade-700 dark:text-bmade-300">
                      {p?.brand ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-bmade-800 dark:text-bmade-200">
                      {r.quantity_available}
                    </td>
                    <td className="px-4 py-3 text-right text-bmade-500 dark:text-bmade-400">
                      {r.quantity_expected || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-bmade-500 dark:text-bmade-400">
                      {r.quantity_reserved || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-bmade-700 dark:text-bmade-300">
                      {r.unit_cost_excl_btw != null ? `EUR ${r.unit_cost_excl_btw.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-bmade-500 dark:text-bmade-400">
                      {r.acquired_date ? new Date(r.acquired_date).toLocaleDateString('nl-NL') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status]}`}
                      >
                        {INVENTORY_STATUS_LABEL[status]}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
